/**
 * /admin/callbacks — table des demandes de rappel ("Être rappelé").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    Loader2, ShieldAlert, Phone, Calendar, Trash2, Clock, Sun, Moon, Sunrise,
} from "lucide-react";


const TIME_LABEL = {
    morning:   "Matin (9h-12h)",
    afternoon: "Après-midi (14h-18h)",
    evening:   "Soir (18h-20h)",
    anytime:   "N'importe quand",
};
const TIME_ICON = { morning: Sunrise, afternoon: Sun, evening: Moon, anytime: Clock };


export default function AdminCallbacks() {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/callback-requests");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onDelete = async (id) => {
        if (!confirm("Marquer comme traité et supprimer ?")) return;
        setDeletingId(id);
        try {
            await api.delete(`/admin/callback-requests/${id}`);
            toast.success("Demande supprimée");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setDeletingId(null);
        }
    };

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour</Link></Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-callbacks-page">
            <p className="em-eyebrow flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Administration</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Demandes de rappel</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Visiteurs ayant cliqué sur "Être rappelé" depuis le header.</p>

            <Card className="rounded-3xl border-border mt-8">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground">
                            <Phone className="h-12 w-12 mx-auto text-primary/30" />
                            <p className="mt-3">Aucune demande de rappel pour l'instant.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[720px]">
                                <thead className="bg-secondary">
                                    <tr className="text-left">
                                        <th className="px-5 py-4 em-eyebrow">Reçue le</th>
                                        <th className="px-5 py-4 em-eyebrow">Nom</th>
                                        <th className="px-5 py-4 em-eyebrow">Téléphone</th>
                                        <th className="px-5 py-4 em-eyebrow">Créneau</th>
                                        <th className="px-5 py-4 em-eyebrow">Sujet</th>
                                        <th className="px-5 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((c) => {
                                        const TimeIcon = TIME_ICON[c.best_time] || Clock;
                                        return (
                                            <tr key={c.id} className="border-t border-border hover:bg-muted/40">
                                                <td className="px-5 py-4 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                                        {new Date(c.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <p className="font-display font-bold">{c.full_name}</p>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <a href={`tel:${c.phone}`} className="font-mono font-bold text-primary hover:underline inline-flex items-center gap-1.5">
                                                        <Phone className="h-3.5 w-3.5" />{c.phone}
                                                    </a>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <span className="inline-flex items-center gap-1.5 bg-secondary px-2.5 py-1 rounded-full text-xs">
                                                        <TimeIcon className="h-3 w-3" />
                                                        {TIME_LABEL[c.best_time] || c.best_time}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-sm text-muted-foreground max-w-[260px] truncate">{c.topic || "—"}</td>
                                                <td className="px-5 py-4 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onDelete(c.id)}
                                                        disabled={deletingId === c.id}
                                                        className="rounded-full text-destructive hover:bg-destructive/10"
                                                    >
                                                        {deletingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
